import { useLazyQuery } from '@apollo/client';
import { VizQueries } from '../../common/graphql';
import { Viz } from '../VizUtil';
import _, {
  constant,
  filter,
  isEmpty,
  isError,
  isFunction,
  isUndefined,
  noop,
  omit,
  pick,
  reject,
  without,
} from 'lodash';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  postModifyVariables,
  scrubNulls,
  vizQueryCallbacks,
  VizQueryPromiseData,
} from '../../common/redux/actions/DiscoverActions';
import Const from '../../common/redux/actions/ActionConst';
import { useDispatch } from 'react-redux';
import { IViz } from '../interfaces';
import {
  LazyQueryExecFunction,
  QueryResult,
} from '@apollo/client/react/types/types';
import { buildLayoutSignature, VizContext } from '../viz-chart/viz-chart.hook';
import {
  useHiddenFieldWarnings,
  useOpenVizChartSpec,
  useVizOptionSelector,
} from '../../common/redux/selectors/viz-selector.hook';
import { ChartSpecIds, VIZ_OPTION_IDS } from '../../common/Constants';
import { useHasValueChanged } from '../../common/utilities/state-helpers.hook';
import { Dispatch } from 'redux';

export const RequeryContext = createContext<{
  showReQuery: boolean;
  reset: () => void;
}>({
  showReQuery: false,
  reset: noop,
});

export const RequeryProvider = ({ children }) => {
  const { id: vizId } = use(VizContext);

  const useLiveQuery = useVizOptionSelector({
    discoveryId: vizId,
    option: VIZ_OPTION_IDS.useLiveQuery,
  });
  const liveQueryHasChanged = useHasValueChanged({
    value: useLiveQuery,
    defaultValue: useLiveQuery,
  });

  // do not show requery on first load
  const [showReQuery, setShowReQuery] = useState(false);

  if (!useLiveQuery && liveQueryHasChanged && !showReQuery) {
    setShowReQuery(true);
  }

  const onVizChange = useCallback(() => {
    if (!showReQuery) {
      setShowReQuery(true);
    }
  }, [showReQuery]);

  useVizWatch(!useLiveQuery && onVizChange);

  const reset = useCallback(() => {
    setShowReQuery(false);
  }, []);

  return (
    <RequeryContext.Provider
      value={{
        showReQuery,
        reset,
      }}
    >
      {children}
    </RequeryContext.Provider>
  );
};

export const emptyPromise = new Promise<QueryResult>(resolve =>
  resolve(undefined),
);

export const emptyVizQueryData = {
  primaryData: undefined,
  secondaryData: undefined,
};
export const emptyQueryDataPromise = new Promise<VizQueryPromiseData>(resolve =>
  resolve(emptyVizQueryData),
);

/**
 * useVizGraphqlPromise returns the awaitable graphql call for a viz query. It
 * doesn't fire any graphql event
 * @param vizId
 * @param skipAnalyze
 */
export const useVizGraphqlPromise = ({ skipAnalyze = false } = {}) => {
  const [viz, setViz] = useState<IViz>(undefined);
  const [primaryVariables, setPrimaryVariables] = useState<object>();
  const [secondaryVariables, setSecondaryVariables] = useState<object>();
  const chartSpec = useOpenVizChartSpec({ discoveryId: viz?.id });

  // build new graphql promise only when this hook says it should update
  useVizWatch((_viz: IViz) => {
    setViz(_viz);
    const _primaryQueryVariables = Viz.buildQueryVariables(_viz as IViz);
    setPrimaryVariables(_primaryQueryVariables);

    const _secondaryQueryVariables =
      isFunction(chartSpec?.buildSecondaryQueryVariables) &&
      chartSpec.buildSecondaryQueryVariables(_viz, _primaryQueryVariables);
    setSecondaryVariables(_secondaryQueryVariables);
  });

  const dispatch = useDispatch();
  const setWarnings = useCallback(
    queryWarnings => {
      dispatch({
        type: Const.Discover.VIZ_QUERY_WARNING,
        discoveryId: viz?.id,
        loading: false,
        warnings: queryWarnings,
        queryVariables: primaryVariables,
        queryId: '',
      });
    },
    [dispatch, primaryVariables, viz?.id],
  );

  const onVizQueryError = useCallback(
    (queryOptions, errorMessage) => {
      dispatch({
        type: Const.Discover.VIZ_QUERY_ERROR,
        discoveryId: viz?.id,
        loading: false,
        error: errorMessage,
        queryOptions,
        queryId: '',
      });
    },
    [viz?.id, dispatch],
  );

  const [
    analyzeGraphQlVizQuery,
    { called: _called, loading: _loading, data: _data },
  ] = useLazyQuery(VizQueries.AnalyzeQuery, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const warnings = _.get(data, 'analyze', []);
      !isEmpty(warnings) && setWarnings(warnings);
    },
    onError: apolloError => {
      setWarnings([
        {
          type: 'GraphQL',
          message: apolloError.message,
        },
      ]);
    },
  });

  const [
    executeGraphqlVizQuery,
    { called: _executeCalled, loading: _executeLoading, data: _executeData },
  ] = useLazyQuery(VizQueries.VizQuery, {
    fetchPolicy: 'network-only',
    onError: apolloError =>
      onVizQueryError(primaryVariables, apolloError.message),
  });

  const { showReQuery } = use(RequeryContext);
  const { id: vizId } = use(VizContext);
  const { isBlocked } = useHiddenFieldWarnings({ discoveryId: vizId });
  // Read after the analysis resolves: the report may have become blocked meanwhile.
  const isBlockedRef = useRef(isBlocked);
  isBlockedRef.current = isBlocked;

  return useMemo<Promise<VizQueryPromiseData>>(() => {
    // early return statements
    if (
      isUndefined(viz) ||
      isEmpty(primaryVariables) ||
      !isFunction(analyzeGraphQlVizQuery) ||
      !isFunction(executeGraphqlVizQuery) ||
      showReQuery ||
      isBlocked
    ) {
      return emptyQueryDataPromise;
    }

    const doAnalyze = skipAnalyze
      ? constant(emptyPromise)
      : () => analyzeGraphQlVizQuery({ variables: primaryVariables });

    const { onComplete, onError } = vizQueryCallbacks({
      vizId: viz?.id,
      dispatch,
    });

    const futureVizQueries = async () => {
      if (isBlockedRef.current) {
        return emptyVizQueryData;
      }
      const lazyVizQueries = await buildExecuteQueryPromises({
        viz,
        variablesArray: [primaryVariables, secondaryVariables],
        lazyQuery: executeGraphqlVizQuery,
        dispatch,
      });

      return Promise.all(lazyVizQueries)
        .then(promiseResults => onComplete(promiseResults, true))
        .catch(e => onError(e, primaryVariables));
    };

    return doAnalyze()
      .then(futureVizQueries)
      .catch(e => {
        onError(e);
        return emptyQueryDataPromise;
      });
  }, [
    analyzeGraphQlVizQuery,
    dispatch,
    executeGraphqlVizQuery,
    primaryVariables,
    secondaryVariables,
    skipAnalyze,
    viz,
    showReQuery,
    isBlocked,
  ]);
};

const buildExecuteQueryPromises = async ({
  viz,
  variablesArray,
  lazyQuery,
  dispatch,
}: {
  viz: IViz;
  variablesArray: any[];
  lazyQuery: LazyQueryExecFunction<any, any>;
  dispatch: Dispatch;
}): Promise<Promise<QueryResult>[]> => {
  const lazyVizQueries: Promise<QueryResult>[] = [];

  for (const variables of reject(variablesArray, isEmpty)) {
    const { variables: _variables } =
      (await postModifyVariables({
        viz: viz as IViz,
        variables,
      })) ?? {};

    _variables.dispatch && dispatch(_variables.dispatch);

    const _lazyQueryPromise = lazyQuery({
      variables: _variables,
    });

    lazyVizQueries.push(_lazyQueryPromise);
  }

  return lazyVizQueries;
};

/**
 * useVizWatch - primary initiator for a viz query. It does this by creating a
 * React lifecycle when any of the watched values change
 */
export const useVizWatch = (callback = undefined, ignoreOptions = []) => {
  const callbackRef = useRef<(viz?: IViz) => void>(callback);
  callbackRef.current = callback;

  const viz = use(VizContext);

  const layoutSignature = useMemo(
    () =>
      buildLayoutSignature(
        omit(viz?.layout, ChartSpecIds.SLICER),
        constant('name'),
      ),
    [viz?.layout],
  );

  const watchedOptionSignature = useMemo(() => {
    const watchedOptionKeys = without(
      [
        VIZ_OPTION_IDS.calcFields,
        VIZ_OPTION_IDS.filters,
        VIZ_OPTION_IDS.useFiscalCalendar,
        VIZ_OPTION_IDS.slicerSelections,
        VIZ_OPTION_IDS.useLiveQuery,
        VIZ_OPTION_IDS.fieldMetadata,
      ],
      ...ignoreOptions,
    );
    return pick(viz?.options, watchedOptionKeys);
  }, [ignoreOptions, viz?.options]);

  const hasLayoutSignatureChanged = useHasValueChanged({
    value: layoutSignature,
    defaultValue: layoutSignature,
  });
  const hasWatchedOptionSignatureChanged = useHasValueChanged({
    value: watchedOptionSignature,
    defaultValue: watchedOptionSignature,
  });
  const hasChartTypeChanged = useHasValueChanged({
    value: viz?.chartType,
    defaultValue: viz?.chartType,
  });

  const fireCallback = useCallback(() => {
    isFunction(callbackRef.current) && callbackRef.current(viz as IViz);
  }, [viz]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fireCallback, []);

  /**
   *  @NOTE critical - this if condition triggers event, but prevents React from propagating lifecycle update
   *  @NOTE DO NOT IMPLEMENT useEffect here because we do not want to trigger lifecycle events for parent hooks
   */
  if (
    filter(
      [
        hasLayoutSignatureChanged,
        hasWatchedOptionSignatureChanged,
        hasChartTypeChanged,
      ],
      Boolean,
    )?.length > 0
  ) {
    fireCallback();
  }
};

/**
 * Custom hook that provides a callback function to fetch query results with subtotals enabled.
 * This is primarily used for Excel export functionality where subtotal rows need to be included
 * in the exported data.
 *
 * The hook executes a separate GraphQL viz query independent of the one used for visualization
 * rendering, with the subtotals flag set to true. This bypasses Redux state updates to avoid
 * interfering with the current visualization display.
 *
 * @param {Object} params - Hook parameters
 * @param {IViz} params.viz - The visualization object containing:
 *   - id: Unique identifier for the viz
 *   - name: Display name of the visualization
 *   - chartType: Type of chart (e.g., 'bar', 'line', 'table')
 *   - layout: Chart layout configuration including dimensions and measures
 *   - options: Additional viz options like filters, calculated fields
 *
 * @returns {Function} fetchQueryWithSubtotals - A callback function that when invoked:
 *   - Returns a Promise that resolves to QueryResult | null
 *   - Executes the query with subtotals enabled
 *   - Returns null if viz is not provided or the query fails
 *   - Scrubs null values from the result data
 *   - Logs detailed error information on failure (vizId, vizName, chartType, error details)
 */
export const useQueryWithSubtotals = ({ viz }: { viz?: IViz }) => {
  const [executeGraphqlVizQuery] = useLazyQuery(VizQueries.VizQuery, {
    fetchPolicy: 'network-only',
  });
  const { isBlocked } = useHiddenFieldWarnings({ discoveryId: viz?.id });

  const fetchQueryWithSubtotals = useCallback(async () => {
    if (!viz || isBlocked) {
      return null;
    }

    try {
      // Generate query variables with subtotals for export
      const variablesWithSubtotals = Viz.buildQueryVariables(viz, true);

      // Execute the query with subtotals directly without updating Redux.
      // `export: true` lets ui-service count the export (discover_exports_total)
      // server-side, where a blocked browser telemetry call cannot hide it.
      const result = await executeGraphqlVizQuery({
        variables: { ...variablesWithSubtotals, export: true },
      });
      const scrubbedData = scrubNulls({
        queryData: [result],
        replaceNulls: true,
      });
      return scrubbedData[0] || null;
    } catch (error) {
      console.error('Failed to fetch query results with subtotals', {
        vizId: viz?.id,
        vizName: viz?.name,
        chartType: viz?.chartType,
        error: isError(error) ? error.message : String(error),
        stack: isError(error) ? error.stack : undefined,
      });
      return null;
    }
  }, [viz, isBlocked, executeGraphqlVizQuery]);

  return fetchQueryWithSubtotals;
};
